import { DeleteOutlined, DownloadOutlined, PlusOutlined, ReloadOutlined, RollbackOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Modal, Space, Spin, Tooltip, Typography, Upload, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { downloadBlob, serverFetch, serverFetchBinary, serverFetchMultipart } from "../../core/server";
import "./database-blocks.css";

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("ru-RU");
};

const formatFileSize = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) return "";
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / 1024 / 1024).toFixed(1)} МБ`;
};

const DatabaseBackups = () => {
  const session = useSelector((state) => state.user.session);
  const canDelete = !!session?.isSuperAdmin;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operation, setOperation] = useState("");

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const result = await serverFetch("app/database/backups");
      setItems(Array.isArray(result) ? result : []);
    } catch (e) {
      setItems([]);
      message.error(e.userMessage || e.message || "Не удалось загрузить резервные копии");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const restoreFromFile = (file) => {
    if (!file) return;
    const name = file.name || "backup.zip";
    if (!/\.zip$/i.test(name)) {
      message.error("Выберите zip-архив с файлом temp.gbk");
      return;
    }
    Modal.confirm({
      title: "Восстановить базу из файла?",
      content: `${name}. Текущая база будет заменена. Операция может занять несколько минут.`,
      okText: "Восстановить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        setOperation("upload");
        try {
          const data = new FormData();
          data.append("file", file, name);
          await serverFetchMultipart("app/database/backups/restore-upload", data);
          message.success("База восстановлена, структура обновлена");
          await loadItems();
        } catch (e) {
          message.error(e.userMessage || e.message || "Не удалось восстановить базу");
          return Promise.reject();
        } finally {
          setOperation("");
        }
      }
    });
  };

  const createBackup = async () => {
    setOperation("create");
    try {
      await serverFetch("app/database/backups", { method: "POST" });
      message.success("Резервная копия создана");
      await loadItems();
    } catch (e) {
      message.error(e.userMessage || e.message || "Не удалось создать копию");
    } finally {
      setOperation("");
    }
  };

  const downloadBackup = async (fileName) => {
    setOperation(`download:${fileName}`);
    try {
      const response = await serverFetchBinary("app/database/backups/download", {
        queryParams: { fileName }
      });
      await downloadBlob(response, fileName);
    } catch (e) {
      message.error(e.userMessage || e.message || "Не удалось скачать копию");
    } finally {
      setOperation("");
    }
  };

  const restoreBackup = (fileName) => {
    Modal.confirm({
      title: "Восстановить базу из копии?",
      content: "Текущая база будет заменена. Операция может занять несколько минут.",
      okText: "Восстановить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        setOperation(`restore:${fileName}`);
        try {
          await serverFetch("app/database/backups/restore", { method: "POST", bodyData: { fileName } });
          message.success("База восстановлена, структура обновлена");
          await loadItems();
        } catch (e) {
          message.error(e.userMessage || e.message || "Не удалось восстановить базу");
          return Promise.reject();
        } finally {
          setOperation("");
        }
      }
    });
  };

  const deleteBackup = (fileName) => {
    Modal.confirm({
      title: "Удалить резервную копию?",
      content: fileName,
      okText: "Удалить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        setOperation(`delete:${fileName}`);
        try {
          await serverFetch("app/database/backups", { method: "DELETE", bodyData: { fileName } });
          message.success("Копия удалена");
          await loadItems();
        } catch (e) {
          message.error(e.userMessage || e.message || "Не удалось удалить копию");
          return Promise.reject();
        } finally {
          setOperation("");
        }
      }
    });
  };

  return (
    <div>
      <Space className="db-toolbar" size={8} wrap>
        <Button type="primary" size="small" icon={<PlusOutlined />} loading={operation === "create"} onClick={createBackup}>
          Создать копию
        </Button>
        <Upload accept=".zip" showUploadList={false} beforeUpload={(file) => { restoreFromFile(file); return false; }}>
          <Button size="small" icon={<UploadOutlined />} loading={operation === "upload"} disabled={!!operation}>
            Из файла
          </Button>
        </Upload>
        <Button size="small" icon={<ReloadOutlined />} onClick={loadItems} loading={loading} />
      </Space>
      <Spin spinning={loading}>
        {items.length === 0 && !loading ? (
          <Typography.Paragraph type="secondary" className="db-empty">
            Резервных копий нет
          </Typography.Paragraph>
        ) : (
          <div className="db-blocks">
            {items.map((row) => (
              <div key={row.fileName} className="db-block" style={{ cursor: "default" }}>
                <div className="db-block__body">
                  <div className="db-block__title">
                    <span className="db-block__title-text">{row.fileName}</span>
                  </div>
                  <div className="db-block__meta">
                    <span>{formatDateTime(row.createdAt)}</span>
                    <span>{formatFileSize(row.size)}</span>
                  </div>
                </div>
                <div className={`db-block__actions${canDelete ? " db-block__actions--wide" : ""}`}>
                  <Tooltip title="Скачать">
                    <Button
                      type="text"
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={operation === `download:${row.fileName}`}
                      onClick={() => downloadBackup(row.fileName)}
                    />
                  </Tooltip>
                  <Tooltip title="Восстановить">
                    <Button type="text" size="small" danger icon={<RollbackOutlined />} onClick={() => restoreBackup(row.fileName)} />
                  </Tooltip>
                  {canDelete && (
                    <Tooltip title="Удалить">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteBackup(row.fileName)} />
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Spin>
    </div>
  );
};

export default DatabaseBackups;
